var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useRef, useState } from 'react';
import { LibraryIcon, PencilIcon, TagIcon } from '@heroicons/react/solid';
// Sentinel for the "Untagged" pseudo-tag - distinct from `null` (which means
// "All items"), and not a value any real tag name could collide with.
export var UNTAGGED = '\0untagged';
export var DRAG_ITEM_ID_TYPE = 'text/rigormining-item-id';
var rowBase = 'flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-sm cursor-pointer select-none';
var rowIdle = 'text-gray-600 hover:bg-gray-100';
var rowActive = 'bg-indigo-50 text-indigo-700 font-medium';
export var TagTree = function (_a) {
    var items = _a.items, selected = _a.selected, onSelect = _a.onSelect, onDropItem = _a.onDropItem, onRenameTag = _a.onRenameTag;
    // The tag being renamed, and the text typed so far.
    var _b = useState(null), editing = _b[0], setEditing = _b[1];
    var _c = useState(''), draft = _c[0], setDraft = _c[1];
    var _d = useState(''), renameError = _d[0], setRenameError = _d[1];
    // Set by Escape, so the blur that follows doesn't save the edit.
    var cancelled = useRef(false);
    var counts = new Map();
    var untaggedCount = 0;
    items.forEach(function (item) {
        if (item.tags.length === 0) {
            untaggedCount += 1;
            return;
        }
        item.tags.forEach(function (t) { var _a; return counts.set(t.name, ((_a = counts.get(t.name)) !== null && _a !== void 0 ? _a : 0) + 1); });
    });
    var tags = Array.from(counts.entries()).sort(function (a, b) { return a[0].localeCompare(b[0]); });
    var allowDrop = function (e) {
        if (!onDropItem)
            return;
        e.preventDefault();
    };
    var handleDrop = function (tagName) { return function (e) {
        if (!onDropItem)
            return;
        e.preventDefault();
        var itemId = e.dataTransfer.getData(DRAG_ITEM_ID_TYPE);
        if (itemId)
            onDropItem(itemId, tagName);
    }; };
    var startRename = function (name) {
        if (!onRenameTag)
            return;
        cancelled.current = false;
        setEditing(name);
        setDraft(name);
        setRenameError('');
    };
    var finishRename = function () { return __awaiter(void 0, void 0, void 0, function () {
        var from, to, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    from = editing;
                    to = draft.trim();
                    setEditing(null);
                    if (cancelled.current || !from || !onRenameTag || !to || to === from)
                        return [2 /*return*/];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, onRenameTag(from, to)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    err_1 = _a.sent();
                    setRenameError("Couldn't rename \"" + from + "\": " + err_1.message);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var onRenameKeyDown = function (e) {
        if (e.key === 'Enter')
            e.currentTarget.blur();
        if (e.key === 'Escape') {
            cancelled.current = true;
            setEditing(null);
        }
    };
    return (React.createElement("nav", { className: "w-56 shrink-0 border-r border-gray-200 bg-gray-50 px-2 py-4 overflow-y-auto" },
        React.createElement("button", { onClick: function () { return onSelect(null); }, className: rowBase + " w-full " + (selected === null ? rowActive : rowIdle) },
            React.createElement("span", { className: "flex items-center gap-2 truncate" },
                React.createElement(LibraryIcon, { className: "h-4 w-4 flex-shrink-0", "aria-hidden": "true" }),
                "All Items"),
            React.createElement("span", { className: "text-xs text-gray-400" }, items.length)),
        tags.length > 0 && (React.createElement("div", { className: "mt-3" },
            React.createElement("p", { className: "px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide" }, "Projects"),
            React.createElement("ul", { className: "mt-1 space-y-0.5" }, tags.map(function (_a) {
                var name = _a[0], count = _a[1];
                return (React.createElement("li", { key: name, onDragOver: allowDrop, onDrop: handleDrop(name), className: "flex items-center rounded-md text-sm " + (selected === name ? rowActive : rowIdle) },
                    React.createElement("button", { type: "button", onClick: function () { return startRename(name); }, disabled: !onRenameTag, className: "group relative flex-shrink-0 pl-3 pr-2 py-1.5 hover:text-indigo-600 disabled:cursor-default", title: "Rename \"" + name + "\"", "aria-label": "Rename tag " + name },
                        React.createElement(TagIcon, { className: "h-4 w-4 " + (onRenameTag ? 'group-hover:opacity-0' : ''), "aria-hidden": "true" }),
                        onRenameTag && (React.createElement(PencilIcon, { className: "h-4 w-4 absolute left-3 top-1.5 opacity-0 group-hover:opacity-100", "aria-hidden": "true" }))),
                    editing === name ? (React.createElement("input", { autoFocus: true, value: draft, onChange: function (e) { return setDraft(e.target.value); }, onBlur: finishRename, onKeyDown: onRenameKeyDown, onFocus: function (e) { return e.currentTarget.select(); }, className: "flex-1 min-w-0 mr-2 my-0.5 px-1.5 py-0.5 text-sm border border-indigo-300 rounded focus:ring-indigo-500 focus:border-indigo-500", "aria-label": "New name for " + name })) : (React.createElement("button", { onClick: function () { return onSelect(name); }, onDoubleClick: function () { return startRename(name); }, className: "flex flex-1 min-w-0 items-center justify-between gap-2 pr-3 py-1.5 cursor-pointer select-none text-left", title: name },
                        React.createElement("span", { className: "truncate" }, name),
                        React.createElement("span", { className: "text-xs text-gray-400 flex-shrink-0" }, count)))));
            })),
            renameError && React.createElement("p", { className: "px-3 mt-1 text-xs text-red-600" }, renameError))),
        untaggedCount > 0 && (React.createElement("button", { onClick: function () { return onSelect(UNTAGGED); }, className: rowBase + " w-full mt-3 " + (selected === UNTAGGED ? rowActive : rowIdle) },
            React.createElement("span", { className: "truncate" }, "Untagged"),
            React.createElement("span", { className: "text-xs text-gray-400" }, untaggedCount)))));
};
export default TagTree;
